'use client';

import './styles/default.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import type Player from 'video.js/dist/types/player';
import { VideoPlayer } from '@/app/elements/video/video-player';
import { useMutedState } from '@/app/elements/video/video-context';
import { detectVideoTypeFromSource } from '@/app/elements/video/video.utils';
import { GameplaySessionVideoSkeleton } from '@/app/components/gameplay-session/subcomponents/gameplay-session-video-skeleton/gameplay-session-video-skeleton';
import { trackGameVideoEvent } from '@/tracking/amplitude/events/general/game-video';
import { useStore } from '@/store/store';
import { classes } from '@/utils/classes/classes';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getPlatformBridge } from '@/logic/bridges';

type GameplaySessionVideoProps = {
    uid: string;
    videoId: string;
    src: string;
    poster?: string;
};

const getVideoOptions = (src: string, poster: string) => ({
    autoplay: true,
    controls: true,
    muted: true,
    fluid: true,
    responsive: true,
    preload: 'auto',
    sources: [
        {
            src,
            type: detectVideoTypeFromSource(src)
        }
    ],
    poster,
    playsinline: true,
    inactivityTimeout: 2000,
    children: ['PosterImage', 'MediaLoader'],
    controlBar: {
        skipButtons: { forward: 10, backward: 10 },
        children: [
            'playToggle',
            'skipBackward',
            'skipForward',
            'muteToggle',
            'progressControl',
            'currentTimeDisplay',
            'timeDivider',
            'durationDisplay',
            'fullscreenToggle'
        ]
    }
});

export const GameplaySessionVideo = ({ uid, videoId, src, poster = '' }: GameplaySessionVideoProps) => {
    const { isMuted, setIsMuted } = useMutedState();
    const { isAndroidWrapper, isXsellWrapper } = useGlobalContext();
    const videoRef = useRef<Player | null>(null);
    const divRef = useRef<HTMLDivElement>(null);
    const hasSentAutoplayEvent = useRef(false);
    const [showSkeleton, setShowSkeleton] = useState(true);

    const handlePlayerReady = useCallback(
        (player: Player) => {
            videoRef.current = player;
            hasSentAutoplayEvent.current = false;
            setShowSkeleton(false);

            setIsMuted(!!player.muted());
            player.on('volumechange', () => setIsMuted(!!player.muted()));

            player.on('play', () => {
                if (hasSentAutoplayEvent.current) return;
                hasSentAutoplayEvent.current = true;
                const result = trackGameVideoEvent('gameVideoAutoPlayed', divRef);
                const { sendEvent } = useStore.getState().tracking;
                sendEvent([result.event]);
            });

            player.on('ended', () => {
                const result = trackGameVideoEvent('gameVideo100pcViewed', divRef);
                const { sendEvent } = useStore.getState().tracking;
                sendEvent([result.event]);
            });
        },
        [setIsMuted]
    );

    useEffect(() => {
        videoRef.current?.muted?.(isMuted);
    }, [isMuted]);

    // only for standalone wrapper for now since xsell does not implement onShowChromeCustomView
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;
        if (!isAndroidWrapper || isXsellWrapper) return undefined;

        const bridge = getPlatformBridge();
        bridge.exec('setGameplayOrientationUnlocked', true);

        const mql = window.matchMedia('(orientation: landscape)');

        const handleOrientationChange = (event: MediaQueryListEvent) => {
            const player = videoRef.current;

            if (!player || player.isDisposed()) return;

            try {
                if (event.matches && !player.isFullscreen()) {
                    player.requestFullscreen();
                } else if (!event.matches && player.isFullscreen()) {
                    player.exitFullscreen();
                }
            } catch (error) {
                console.warn('gameplay-session-video orientation fullscreen toggle failed', error);
            }
        };

        mql.addEventListener('change', handleOrientationChange);
        return () => {
            mql.removeEventListener('change', handleOrientationChange);
            bridge.exec('setGameplayOrientationUnlocked', false);
        };
    }, [isAndroidWrapper, isXsellWrapper]);

    return (
        <div className="gameplay-session-video" ref={divRef} data-uid={uid}>
            {showSkeleton && <GameplaySessionVideoSkeleton placeholderUrl={poster} />}
            <div className={classes('gameplay-session-video-inner', showSkeleton && 'gameplay-session-video-hidden')}>
                <div id={videoId} className="gameplay-session-video-player">
                    <VideoPlayer options={getVideoOptions(src, poster)} onReady={handlePlayerReady} videoId={videoId} />
                </div>
            </div>
        </div>
    );
};
